import React, { useEffect, useRef, useState } from 'react';
import polyline from '@mapbox/polyline';

const MapPreview = ({ routePolyline, originAddress, destinationAddress }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const currentMarkerRef = useRef(null);
  const [totalDistance, setTotalDistance] = useState(0);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [navInstructions, setNavInstructions] = useState("");

  // Hàm giải mã encoded polyline: polyline.decode trả về mảng [[lat, lng], ...]
  // Nhưng goongjs yêu cầu tọa độ theo dạng [[lng, lat], ...]
  const decodePolyline = (encoded) => {
    try {
      return polyline.decode(encoded).map(([lat, lng]) => [lng, lat]);
    } catch (err) {
      console.error("Lỗi khi giải mã polyline:", err);
      return [];
    }
  };

  // Hàm tính khoảng cách giữa 2 điểm theo công thức haversine
  const haversineDistance = (coord1, coord2) => {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const [lng1, lat1] = coord1;
    const [lng2, lat2] = coord2;
    const R = 6371; // km
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
              Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Khởi tạo bản đồ (chỉ khởi tạo 1 lần)
  useEffect(() => {
    if (!window.goongjs) {
      console.error('goongjs chưa được nhúng. Hãy thêm script CDN vào index.html.');
      return;
    }
    window.goongjs.accessToken =
      process.env.REACT_APP_GOONG_MAP_TILES_KEY || 'your_map_tiles_key';
    const map = new window.goongjs.Map({
      container: mapContainerRef.current,
      style: 'https://tiles.goong.io/assets/goong_map_web.json',
      center: [105.8, 21.02],
      zoom: 6,
    });
    mapRef.current = map;
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, []);

  // Theo dõi vị trí hiện tại liên tục
  useEffect(() => {
    let watchId;
    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          const pos = [position.coords.longitude, position.coords.latitude];
          setCurrentPosition(pos);
        },
        (error) => {
          console.error("Lỗi khi lấy vị trí hiện tại:", error);
        },
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
      );
    } else {
      console.error("Trình duyệt không hỗ trợ định vị địa lý.");
    }
    return () => {
      if (navigator.geolocation && watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, []);

  // Cập nhật marker cho vị trí hiện tại
  useEffect(() => {
    if (!mapRef.current || !currentPosition) return;
    const map = mapRef.current;
    if (currentMarkerRef.current) {
      currentMarkerRef.current.setLngLat(currentPosition);
    } else {
      const currentEl = document.createElement('div');
      currentEl.style.backgroundColor = '#FFD700'; // Màu vàng
      currentEl.style.padding = '4px 8px';
      currentEl.style.borderRadius = '4px';
      currentEl.style.color = '#000';
      currentEl.style.fontSize = '12px';
      currentEl.innerText = 'Vị trí của bạn';
      currentMarkerRef.current = new window.goongjs.Marker(currentEl)
        .setLngLat(currentPosition)
        .addTo(map);
    }
  }, [currentPosition]);

  // Khi routePolyline thay đổi, thêm layer định tuyến, marker và tính tổng quãng đường
  useEffect(() => {
    if (!mapRef.current || !routePolyline) return;
    const map = mapRef.current;
    const coordinates = decodePolyline(routePolyline);

    // Tính tổng quãng đường
    if (coordinates.length > 1) {
      let distance = 0;
      for (let i = 1; i < coordinates.length; i++) {
        distance += haversineDistance(coordinates[i - 1], coordinates[i]);
      }
      setTotalDistance(distance);
    } else {
      setTotalDistance(0);
    }

    if (!coordinates || coordinates.length < 2) return;

    const addRouteLayer = () => {
      if (map.getSource('route')) {
        if (map.getLayer('route')) {
          map.removeLayer('route');
        }
        map.removeSource('route');
      }
      map.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: coordinates,
          },
        },
      });
      map.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        paint: {
          'line-color': '#FF0000',
          'line-width': 4,
        },
      });
      const bounds = new window.goongjs.LngLatBounds();
      coordinates.forEach(coord => bounds.extend(coord));
      map.fitBounds(bounds, { padding: 40 });
    };

    const addMarkers = () => {
      const startCoord = coordinates[0];
      const endCoord = coordinates[coordinates.length - 1];

      const startEl = document.createElement('div');
      startEl.style.backgroundColor = '#00FF00';
      startEl.style.padding = '4px 8px';
      startEl.style.borderRadius = '4px';
      startEl.style.color = '#000';
      startEl.style.fontSize = '12px';
      startEl.innerText = originAddress || 'Điểm đi';
      new window.goongjs.Marker(startEl)
        .setLngLat(startCoord)
        .addTo(map);

      const endEl = document.createElement('div');
      endEl.style.backgroundColor = '#0000FF';
      endEl.style.padding = '4px 8px';
      endEl.style.borderRadius = '4px';
      endEl.style.color = '#fff';
      endEl.style.fontSize = '12px';
      endEl.innerText = destinationAddress || 'Điểm đến';
      new window.goongjs.Marker(endEl)
        .setLngLat(endCoord)
        .addTo(map);
    };

    const addEverything = () => {
      addRouteLayer();
      addMarkers();
    };

    if (map.isStyleLoaded()) {
      addEverything();
    } else {
      const onLoad = () => {
        addEverything();
        map.off('load', onLoad);
      };
      map.on('load', onLoad);
    }
  }, [routePolyline, originAddress, destinationAddress]);

  // Cập nhật hướng dẫn di chuyển dựa trên vị trí hiện tại và route
  useEffect(() => {
    if (!currentPosition || !routePolyline) return;
    const coordinates = decodePolyline(routePolyline);
    if (!coordinates.length) return;
    // Tìm điểm trên route gần nhất với vị trí hiện tại
    let minIndex = 0;
    let minDist = Infinity;
    for (let i = 0; i < coordinates.length; i++) {
      const d = haversineDistance(currentPosition, coordinates[i]);
      if (d < minDist) {
        minDist = d;
        minIndex = i;
      }
    }
    if (minIndex < coordinates.length - 1) {
      const nextCoord = coordinates[minIndex + 1];
      const distanceToNext = haversineDistance(currentPosition, nextCoord) * 1000; // mét
      // Giả sử hướng dẫn đơn giản: "Tiếp tục đi thẳng khoảng X mét"
      setNavInstructions(`Tiếp tục đi thẳng khoảng ${distanceToNext.toFixed(0)} mét`);
    } else {
      setNavInstructions("Bạn đã đến điểm cuối");
    }
  }, [currentPosition, routePolyline]);

  return (
    <div>
      <div ref={mapContainerRef} style={{ width: '100%', height: '400px' }} />
      <div style={{ marginTop: '1rem' }}>
        <p><b>Điểm đi:</b> {originAddress || 'N/A'}</p>
        <p><b>Điểm đến:</b> {destinationAddress || 'N/A'}</p>
        <p><b>Tổng quãng đường:</b> {totalDistance.toFixed(2)} km</p>
        <p><b>Hướng dẫn di chuyển:</b> {navInstructions}</p>
      </div>
    </div>
  );
};

export default MapPreview;
